/**
 * Working-hours presets and the edit plan — pure rules over the verified
 * availability contract (cms-backend availability/*): one weekly range per
 * POST, a range is refused (409) when it overlaps another of the same day,
 * ranges are removed one by one, and there is no edit endpoint. Presets are a
 * frontend shortcut only (Product 2026-09-07): six of them, fixed at
 * 08:00–20:00, and applying one REPLACES the person's current schedule.
 */

import { toAsciiDigits } from '@/core/i18n/digits';
import type { DeliveryMode } from '@/core/clinic/setupApi';

/** ISO weekdays (1 = Monday … 7 = Sunday), Saturday-first for the clinic week. */
export const DAY_IDS = [6, 7, 1, 2, 3, 4, 5] as const;

export const PRESET_PATTERNS = ['all_week', 'even_days', 'odd_days'] as const;
export type PresetPattern = (typeof PRESET_PATTERNS)[number];
export const PRESET_LOCATIONS = ['in_clinic', 'off_site'] as const;
export type PresetLocation = (typeof PRESET_LOCATIONS)[number];

/** Friday (5) is deliberately in neither the even nor the odd pattern. */
export const PRESET_DAYS: Record<PresetPattern, readonly number[]> = {
  all_week: [6, 7, 1, 2, 3, 4, 5],
  even_days: [6, 1, 3],
  odd_days: [7, 2, 4],
};
export const PRESET_START_MINUTE = 8 * 60;
export const PRESET_END_MINUTE = 20 * 60;

export interface RangeLike {
  id: string;
  dayOfWeek: number;
  startMinute: number;
  endMinute: number;
}

export interface RangeInput {
  dayOfWeek: number;
  startMinute: number;
  endMinute: number;
  /** ZOY-237: the modes worked in during the range — at least one. */
  modes: DeliveryMode[];
}

/** A preset row is a physical place; online (ZOY-693) can be added on top. */
export function presetModes(location: PresetLocation, online: boolean): DeliveryMode[] {
  return online ? [location, 'online'] : [location];
}

export function presetRanges(pattern: PresetPattern, modes: readonly DeliveryMode[]): RangeInput[] {
  return PRESET_DAYS[pattern].map((dayOfWeek) => ({
    dayOfWeek,
    startMinute: PRESET_START_MINUTE,
    endMinute: PRESET_END_MINUTE,
    modes: [...modes],
  }));
}

/** Replace: every range currently set goes, then the preset's ranges come. */
export function planPreset(
  existing: readonly RangeLike[],
  pattern: PresetPattern,
  modes: readonly DeliveryMode[],
): { remove: string[]; add: RangeInput[] } {
  return { remove: existing.map((r) => r.id), add: presetRanges(pattern, modes) };
}

export const rangesOverlap = (
  a: Pick<RangeLike, 'dayOfWeek' | 'startMinute' | 'endMinute'>,
  b: Pick<RangeLike, 'dayOfWeek' | 'startMinute' | 'endMinute'>,
): boolean =>
  a.dayOfWeek === b.dayOfWeek && a.startMinute < b.endMinute && b.startMinute < a.endMinute;

/**
 * A change is two writes. When the new range would collide with the old one
 * the old must go first (else the backend refuses the new as an overlap) and
 * the old is put back if the new is refused; otherwise the new goes in first
 * and the old is removed only once the new exists — nothing is lost either way.
 */
export type EditPlan = 'add-then-remove' | 'remove-then-add';
export function editPlan(
  old: Pick<RangeLike, 'dayOfWeek' | 'startMinute' | 'endMinute'>,
  next: Pick<RangeLike, 'dayOfWeek' | 'startMinute' | 'endMinute'>,
): EditPlan {
  return rangesOverlap(old, next) ? 'remove-then-add' : 'add-then-remove';
}

export function minuteToHHMM(m: number): string {
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return `${String(h).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
}

export function hhmmToMinute(v: string): number | null {
  // ZOY-314: Persian/Arabic-Indic digits count as digits here too.
  const m = /^(\d{1,2}):(\d{2})$/.exec(toAsciiDigits(v.trim()));
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h > 23 || min > 59) return null;
  return h * 60 + min;
}
